// Display-only shortcut formatting for the RichEditor toolbar. The counterpart
// to the `metaKey || ctrlKey` behavior check in LinkDialogPlugin: this decides
// only how a shortcut is rendered, never how a key event is handled.

// A `mod` token renders as ⌘ on macOS and Ctrl elsewhere. `shift` renders as
// ⇧ / Shift, `alt` as ⌥ / Alt. Any other token is a literal key, uppercased.
const SYMBOL = { mod: "⌘", shift: "⇧", alt: "⌥" };
const WORD = { mod: "Ctrl", shift: "Shift", alt: "Alt" };

export function isMacPlatform() {
  if (typeof navigator === "undefined") {
    return false;
  }
  const marker = navigator.platform || navigator.userAgent || "";
  return /Mac|iPhone|iPad|iPod/i.test(marker);
}

export function formatShortcut(tokens) {
  const mac = isMacPlatform();
  const parts = tokens.map(token => {
    if (token === "mod" || token === "shift" || token === "alt") {
      return mac ? SYMBOL[token] : WORD[token];
    }
    return token.toUpperCase();
  });
  // macOS convention joins the symbols directly (⌘⇧Z); other platforms use the
  // "+" separator (Ctrl+Shift+Z).
  return mac ? parts.join("") : parts.join("+");
}

// aria-keyshortcuts names concrete key combinations, so `mod` expands to both
// WAI-ARIA modifier names (Meta and Control), producing one space-separated
// combination per platform. The binding works on both, so both are announced
// regardless of the current platform.
export function ariaKeyshortcuts(tokens) {
  const resolve = modifier =>
    tokens
      .map(token => {
        if (token === "mod") {
          return modifier;
        }
        if (token === "shift") {
          return "Shift";
        }
        if (token === "alt") {
          return "Alt";
        }
        return token.toUpperCase();
      })
      .join("+");
  return `${resolve("Meta")} ${resolve("Control")}`;
}

const MODIFIER_TOKENS = new Set(["mod", "shift", "alt"]);

// Maps a literal shortcut token to a KeyboardEvent.code value ("X" → "KeyX",
// "7" → "Digit7"). event.code is the physical key, independent of the active
// layout, so a match does not depend on the character Alt would otherwise
// produce on macOS.
function codeForKey(key) {
  return /^[0-9]$/.test(key) ? `Digit${key}` : `Key${key.toUpperCase()}`;
}

// Matches a keyboard event against a token spec. `mod` accepts either Meta or
// Control (the same cross-platform test LinkDialogPlugin uses); `shift` and
// `alt` must match the event exactly, so Ctrl+E does not fire on Ctrl+Shift+E.
export function matchesShortcut(event, tokens) {
  if (!(event.metaKey || event.ctrlKey)) {
    return false;
  }
  if (event.shiftKey !== tokens.includes("shift")) {
    return false;
  }
  if (event.altKey !== tokens.includes("alt")) {
    return false;
  }
  const key = tokens.find(token => !MODIFIER_TOKENS.has(token));
  return event.code === codeForKey(key);
}
